/**
 * Footer Queries - Extended with siteId lookup
 *
 * @override
 * - Re-exports all base footer queries from @amblique/contentful
 * - Adds getFooterBySiteId queries (base, default, full variants)
 * - Adds custom getFooterWithColumnsBySiteId query with explicit limits
 */

import gql from 'graphql-tag';

import {
  BaseFooterFragment,
  FooterFragment,
  FullFooterFragment,
} from '@amblique/contentful/app/contentful/fragments/footer';

// Re-export all base queries
export * from '@amblique/contentful/app/contentful/queries/footer.queries';

/**
 * Custom Footer query with nested columns and links
 * Uses explicit limits to avoid Contentful query complexity errors
 */
export const getFooterWithColumnsBySiteId = gql`
  query GetFooterWithColumnsBySiteId($siteId: String!, $locale: String!, $preview: Boolean) {
    footerCollection(
      where: { siteId: $siteId }
      limit: 1
      locale: $locale
      preview: $preview
    ) {
      items {
        __typename
        sys {
          id
        }
        siteId
        legalText
        aboutUs
        siteNotes
        region
        logo {
          title
          altText
          desktopAsset {
            url
            title
            description
          }
          mobileAsset {
            url
            title
          }
        }
        columnsCollection(limit: 10) {
          items {
            __typename
            sys {
              id
            }
            title
            linksCollection(limit: 20) {
              items {
                sys {
                  id
                }
                text
                url
                openInNewTab
              }
            }
          }
        }
        socialLinksCollection(limit: 10) {
          items {
            sys {
              id
            }
            text
            url
            openInNewTab
            image {
              url
              title
            }
          }
        }
        countriesCollection(limit: 10) {
          items {
            sys {
              id
            }
            text
            url
            openInNewTab
            totalCount
          }
        }
        paymentsCollection(limit: 5) {
          items {
            __typename
            sys {
              id
            }
            ... on IconSet {
              title
              iconsCollection(limit: 20) {
                items {
                  __typename
                  sys {
                    id
                  }
                  title
                  name
                  svgCode
                  mediaType
                  iconImage {
                    url
                    title
                    description
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Get a single Footer by siteId (base - scalars only)
 */
export const getBaseFooterBySiteId = gql`
  ${BaseFooterFragment}

  query GetBaseFooterBySiteId($siteId: String!, $locale: String!, $preview: Boolean) {
    footerCollection(
      where: { siteId: $siteId }
      limit: 1
      locale: $locale
      preview: $preview
    ) {
      items {
        ...BaseFooterFragment
      }
    }
  }
`;

/**
 * Get a single Footer by siteId (standard - 1 level deep)
 */
export const getFooterBySiteId = gql`
  ${FooterFragment}

  query GetFooterBySiteId($siteId: String!, $locale: String!, $preview: Boolean) {
    footerCollection(
      where: { siteId: $siteId }
      limit: 1
      locale: $locale
      preview: $preview
    ) {
      items {
        ...FooterFragment
      }
    }
  }
`;

/**
 * Get a single Footer by siteId (full - 2 levels deep)
 */
export const getFullFooterBySiteId = gql`
  ${FullFooterFragment}

  query GetFullFooterBySiteId($siteId: String!, $locale: String!, $preview: Boolean) {
    footerCollection(
      where: { siteId: $siteId }
      limit: 1
      locale: $locale
      preview: $preview
    ) {
      items {
        ...FullFooterFragment
      }
    }
  }
`;
